// IndexedDB-backed storage for GIF sessions.
//
// Sessions embed base64 frame image data, so they were overflowing the ~5MB
// localStorage quota (Failed to execute 'setItem' … exceeded the quota).
// IndexedDB has orders-of-magnitude more room and is what videos/frames use.
import type { GifSession } from '@/lib/local-types'

const DB_NAME = 'framex-gif-sessions-db'
const DB_VERSION = 1
const STORE = 'sessions'

let dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return dbPromise
}

export async function getAllSessions(): Promise<GifSession[]> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).getAll()
    req.onsuccess = () => resolve((req.result as GifSession[]) || [])
    req.onerror = () => reject(req.error)
  })
}

// Replace the whole set (mirrors the previous "write the array" model).
export async function saveAllSessions(sessions: GifSession[]): Promise<void> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    const store = tx.objectStore(STORE)
    store.clear()
    for (const session of sessions) store.put(session)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error ?? new Error('GIF sessions transaction aborted'))
  })
}
